import {
  Stethoscope, ScanLine, Scissors, Sparkles, Baby, ShieldCheck,
  Zap, Activity, HeartPulse, Spline, Gift, MessageCircle, ArrowUpRight,
} from "lucide-react";
import { CLINIC, DENTAL_SERVICES, FIZIO_SERVICES, MEDIA, buildWaUrl } from "@/lib/data";

const ICONS = { Stethoscope, ScanLine, Scissors, Sparkles, Baby, ShieldCheck, Zap, Activity, HeartPulse, Spline, Gift };

function ServiceCard({ icon, title, text, accent, index, testIdPrefix }) {
  const Icon = ICONS[icon] || ShieldCheck;
  const isBlue = accent === "blue";
  return (
    <div
      data-testid={`${testIdPrefix}-card-${index}`}
      className="group relative flex flex-col h-full bg-white rounded-3xl p-7 lg:p-8 border border-slate-100 card-shadow hover:card-shadow-hover hover:-translate-y-1 transition-all duration-500"
    >
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-colors ${
          isBlue ? "bg-brand-soft text-brand-blue group-hover:bg-brand-blue group-hover:text-white" : "bg-[#F2F9DD] text-brand-aquaDark group-hover:bg-brand-aqua group-hover:text-white"
        }`}
      >
        <Icon className="w-7 h-7" strokeWidth={1.6} />
      </div>
      <h3 className="font-display text-[20px] lg:text-[22px] font-medium text-slate-900 mb-2.5 tracking-tight">
        {title}
      </h3>
      <p className="text-[15px] leading-relaxed text-slate-600 flex-grow">{text}</p>
      <div
        className={`mt-5 flex items-center gap-1.5 text-[13px] font-medium opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0 ${
          isBlue ? "text-brand-blue" : "text-brand-aquaDark"
        }`}
      >
        Află mai mult <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  );
}

function ServicesSection({ id, kicker, title, intro, services, accent, videoSrc, phone, waUrl, sectionBg, testIdPrefix }) {
  const isBlue = accent === "blue";
  return (
    <section
      id={id}
      data-testid={`${testIdPrefix}-section`}
      className={`relative py-20 lg:py-28 ${sectionBg}`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-14 lg:mb-20">
          <div className="lg:col-span-5">
            <span
              className={`inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold mb-5 ${
                isBlue ? "text-brand-blue" : "text-brand-aquaDark"
              }`}
            >
              <span className={`w-6 h-px ${isBlue ? "bg-brand-blue" : "bg-brand-aqua"}`} />
              {kicker}
            </span>
            <h2 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] font-light text-slate-900 tracking-tight mb-6 text-balance">
              {title}
            </h2>
            <p className="text-[16px] lg:text-[17px] leading-relaxed text-slate-600 mb-8">
              {intro}
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-section-cta`}
              className={`inline-flex items-center gap-3 text-white font-medium text-[15px] px-6 py-3.5 rounded-full transition-all shadow-lg hover:-translate-y-0.5 ${
                isBlue
                  ? "bg-brand-blue hover:bg-brand-blueDark shadow-brand-blue/25"
                  : "bg-brand-aqua hover:bg-brand-aquaDark shadow-brand-aqua/25"
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              Programează-te · {phone}
            </a>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="relative rounded-[28px] overflow-hidden aspect-[16/10] card-shadow-hover">
              <video
                src={videoSrc}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid={`${testIdPrefix}-video`}
              />
              <div className={`absolute inset-0 ${isBlue ? "bg-gradient-to-t from-brand-blue/30 via-transparent to-transparent" : "bg-gradient-to-t from-brand-aqua/25 via-transparent to-transparent"}`} />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl px-5 py-4 card-shadow border border-slate-100">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isBlue ? "bg-brand-soft text-brand-blue" : "bg-[#F2F9DD] text-brand-aquaDark"}`}>
                <ShieldCheck className="w-6 h-6" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Standard</p>
                <p className="font-display font-medium text-slate-900">Aparatură modernă</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
          {services.map((s, i) => (
            <ServiceCard
              key={s.title}
              icon={s.icon}
              title={s.title}
              text={s.text}
              accent={accent}
              index={i}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function DentalSection() {
  return (
    <ServicesSection
      id="dental"
      kicker="Marimar Dental Clinic"
      title="Zâmbetul tău este cel mai bun salut."
      intro="La Marimar Dental Clinic, fiecare tratament începe cu un diagnostic atent, explicații clare și un plan adaptat nevoilor tale. Oferim servicii stomatologice pentru adulți și copii, într-un mediu modern, sigur și prietenos."
      services={DENTAL_SERVICES}
      accent="blue"
      videoSrc={MEDIA.dentalVideo}
      phone={CLINIC.dental.phone}
      waUrl={buildWaUrl(CLINIC.dental.waNumber, "Bună ziua! Doresc o programare la Marimar Dental Clinic.")}
      sectionBg="bg-brand-soft/40"
      testIdPrefix="dental"
    />
  );
}

export function FizioSection() {
  return (
    <ServicesSection
      id="fizio"
      kicker="Marimar Fizio & Kinetoterapie"
      title="Recuperarea înseamnă încredere în propriul corp."
      intro="La Marimar Fizio & Kinetoterapie folosim aparatură modernă, evaluare atentă și programe personalizate pentru durere, mobilitate, postură și recuperare medicală."
      services={FIZIO_SERVICES}
      accent="aqua"
      videoSrc={MEDIA.fizioVideo}
      phone={CLINIC.fizio.phone}
      waUrl={buildWaUrl(CLINIC.fizio.waNumber, "Bună ziua! Doresc o programare la Marimar Fizio & Kinetoterapie.")}
      sectionBg="bg-white"
      testIdPrefix="fizio"
    />
  );
}
